import React from 'react';

import Select from 'react-select';
import { Link } from 'react-router-dom';
import { TextInput, Button, Row } from '@cuemath/comps';
import { COUNTRY_OPTIONS, BILLING_COUNTRY_OPTIONS, INDIAN_STATE_OPTIONS } from 'utils';
import styled, { css } from 'styled-components';
import debounce from 'lodash.debounce';
import {
  FIELD_TYPES,
  OPTIONS,
  studentStateOptions,
  teacherStateOptions,
  gradeOptions,
  genderOptions,
  teacherGenderOptions,
  teacherSlotTypeOptions,
  dayOptions,
  trialOptions,
  paymentTenureOptions,
  paymentTypeOptions,
  paymentClientOptions,
  studentDemoStateOptions,
  studentDemoLeadStateOptions,
  parentSourceOptions,
  paymentMethodOptions,
  classRatioOptions,
  classesPerWeekOptions,
  subscriptionCurrencyOptions,
  uploadPaymentCurrencyOptions,
  accessTypeOptions,
  teacherCredStateOptions,
  dynamicClassRatioOptions,
  rmTimezoneOptions,
  rmRegionOptions,
  studentCourseUpdateOptions,
  enrollmentTypeOfflineOptions,
  MAPPING_MODEL_OPTIONS,
} from 'constants/form';
import COLORS from 'constants/colors';
import { COURSE_STATE } from 'constants/student-courses';

export const FormWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const FieldWrapper = styled.div`
  width: ${({ width }) => width || '80%'};
  display: flex;
  flex-direction: column;
  align-items: center;
  ${({ justifyContent }) =>
    justifyContent &&
    css`
      justify-content: ${justifyContent};
    `};
`;

export const FormHead = styled.div`
  width: 80%;
  margin-top: 20px;
`;

export const Head = styled.div`
  width: 100%;
  margin-top: 20px;
`;

export const HeaderHead = styled.div`
  width: 100%;
  margin-top: 20px;
  border: 1px solid #333;
  background: #ccc;
`;

export const CardTableHeader = styled(Row)`
  width: 100%;
  padding-left: 15px;
  padding-right: 20px;
  display: flex;
`;

export const Field = styled.div`
  width: 80%;
  ${({ fieldWidth }) =>
    fieldWidth &&
    css`
      width: ${fieldWidth};
    `};
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  ${({ justifyContent }) =>
    justifyContent &&
    css`
      justify-content: ${justifyContent};
    `};
  margin-bottom: ${({ profileView }) => `${profileView ? '10px' : '12px'}`};
`;

export const Label = styled.label`
  min-width: ${({ profileView }) => `${profileView ? '35%' : '40%'}`};
  padding-left: ${({ profileView }) => `${profileView ? '10px' : '4px'}`};
  ${({ labelMinWidth }) =>
    labelMinWidth &&
    css`
      width: ${labelMinWidth};
    `};
  ${({ paddingLeft }) =>
    paddingLeft &&
    css`
      padding-left: ${paddingLeft};
    `};
`;

export const Asterisk = styled.label`
  color: red;
`;

export const Input = styled(TextInput)`
  width: ${({ profileView }) => `${profileView ? '300px' : '340px'}`};
  height: 40px;
  ${({ width }) =>
    width &&
    css`
      width: ${width}px;
    `};
  ${({ height }) =>
    height &&
    css`
      height: ${height}px;
    `};
  padding: 15px 10px;
  outline: none;
  display: inline-block;
  background-color: ${({ disabled }) => `${disabled ? COLORS.READONLY : COLORS.WHITE}`};
  &:focus {
    border: 3px solid #c4dffe;
    &::placeholder {
      opacity: 0;
    }
  }
  user-select: ${({ disableUserSelect }) => (disableUserSelect ? 'none' : 'auto')};
  pointer-events: ${({ disableUserSelect }) => (disableUserSelect ? 'none' : 'auto')};
`;

export const Checkbox = styled.input`
  width: ${({ profileView }) => `${profileView ? '300px' : '340px'}`};
  height: 20px;
  padding: 15px 10px;
  outline: none;
  display: inline-block;
  background-color: ${({ disabled }) => `${disabled ? COLORS.READONLY : COLORS.WHITE}`};
`;

export const SelectInput = styled(Select)`
  width: ${({ profileView }) => `${profileView ? '300px' : '340px'}`};
  ${({ width }) =>
    width &&
    css`
      width: ${width};
    `};
`;

export const Footer = styled.div`
  width: 80%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const ButtonCenterWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  text-align: center;
`;

export const CenterButton = styled(Button)`
  padding-left: 40px;
  padding-right: 40px;
`;

export const ButtonWrapper = styled.div`
  width: 80%;
  ${({ width }) =>
    width &&
    css`
      width: ${width};
    `};
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: ${({ justifyContent }) => justifyContent || 'space-between'};
`;

const getTabRadius = buttonPosition => {
  switch (buttonPosition) {
    case 'left':
      return '10px 0 0 10px';
    case 'right':
      return '0 10px 10px 0';
    default:
      return '0';
  }
};

export const TabButton = styled(Button)`
  border-radius: ${({ position }) => `${getTabRadius(position)}`};
`;

export const NavigationButtons = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

export const NavButton = styled(Button)`
  margin-left: 4px;
  margin-right: 4px;
`;

export const ErrorRow = styled(Row)`
  justify-content: center;
`;

export const Message = styled.div`
  width: 60%;
  ${({ width }) =>
    width &&
    css`
      width: ${width};
    `};
  margin-top: 30px;
  border: 1px solid;
  border-radius: 5px;
  padding: 5px;
  text-align: center;
  background: ${({ error }) => (error ? COLORS.ERROR : COLORS.SUCCESS)};
`;

export const LinkNav = styled(Link)`
  text-decoration: underline;
  cursor: pointer;
  color: blue;
  text-align: center;
  &:hover {
    color: blue;
  }
  &:active {
    color: blue;
  }
`;

function renderInputField(field) {
  const {
    fieldWidth,
    justifyContent,
    profileView,
    labelMinWidth,
    label,
    required,
    name,
    placeholder,
    type,
    defaultValue,
    value,
    disabled,
    onChange,
    maxlength,
    width,
    display,
    disableUserSelect,
  } = field || {};
  return (
    <Field key={name} fieldWidth={fieldWidth} justifyContent={justifyContent}>
      <Label profileView={profileView} labelMinWidth={labelMinWidth}>
        {label}
        {required ? <Asterisk>*</Asterisk> : null}
      </Label>
      <Input
        name={name}
        placeholder={placeholder}
        type={type}
        required={required}
        value={value || defaultValue}
        onChange={onChange}
        disabled={disabled}
        profileView={profileView || null}
        display={display || null}
        maxLength={maxlength || null}
        width={width}
        disableUserSelect={disableUserSelect || null}
      />
    </Field>
  );
}

function getSelectedValue(options) {
  if (!options) return null;
  return Array.isArray(options) ? options?.map(({ value }) => value) : options.value;
}

export function getFormDefaults(filledData, formFields) {
  const formDefaults = {};

  formFields.forEach(field => {
    formDefaults[field.name] =
      filledData && filledData[field.param] ? filledData[field.param] : field.default;
  });
  return formDefaults;
}

export function getFormFields(data) {
  return data?.map(field => {
    return field;
  });
}

export function validateIsEmpty(input) {
  if (typeof input === 'string') input.trim();
  return input === null || input === '';
}

function renderSelectField(field) {
  const {
    choices,
    disabled,
    label,
    multiple,
    name,
    onChange,
    onInputChange,
    profileView,
    required,
    value,
    width,
    fieldWidth,
    labelMinWidth,
    justifyContent,
  } = field || {};
  let selectedValue = choices.filter(option => {
    return value && Array.isArray(value)
      ? value.includes(option.value)
      : option.value === value;
  });

  if (selectedValue.length && !multiple) selectedValue = selectedValue[0];

  const filterOption = (option, inputValue) => {
    const { label } = option;
    if (typeof label !== 'string') {
      return label;
    }
    const labelOfOptions = label.toUpperCase();

    const otherKey = choices.filter(
      ({ label }) =>
        label === labelOfOptions &&
        label.toUpperCase().includes(inputValue.toUpperCase()),
    );
    return labelOfOptions.includes(inputValue.toUpperCase()) || otherKey.length > 0;
  };

  const inputChange = () => {
    const isTeacherIdField = name === 'teacher_id';

    if (isTeacherIdField && onInputChange) {
      return debounce((val, reason) => onInputChange(val, reason), 1000);
    }
    return null;
  };
  return (
    <Field key={name} fieldWidth={fieldWidth} justifyContent={justifyContent}>
      <Label profileView={profileView} labelMinWidth={labelMinWidth}>
        {label}
        {required ? <Asterisk>*</Asterisk> : null}
      </Label>
      <SelectInput
        required={required}
        name={name}
        value={selectedValue}
        filterOption={filterOption}
        onInputChange={inputChange()}
        onChange={selected => onChange(name, getSelectedValue(selected))}
        isSearchable
        isDisabled={disabled}
        isMulti={!!multiple}
        options={choices}
        width={width}
        maxMenuHeight={200}
      />
    </Field>
  );
}

function renderCheckboxField(field) {
  return (
    <Field key={field.name}>
      <Label profileView={field.profileView}>
        {field.label}
        {field.required ? <Asterisk>*</Asterisk> : null}
      </Label>
      <Checkbox
        name={field.name}
        type={field.type}
        required={field.required}
        value={field.value}
        onChange={field.onChange}
        disabled={field.disabled}
        checked={field.value}
      />
    </Field>
  );
}

export function renderFormField(field) {
  switch (field.fieldType) {
    case FIELD_TYPES.INPUT:
      return renderInputField(field);
    case FIELD_TYPES.SELECT:
      return renderSelectField(field);
    case FIELD_TYPES.CHECKBOX:
      return renderCheckboxField(field);
    default:
      return null;
  }
}

export function getOptions({
  field,
  userConfig,
  batchConfig,
  teachersDataOptions,
  programs,
  currencyOptions,
  onlineBandOptions = [],
  dynamicClassRatio,
  onlinePlanOptions,
  course_type,
  addonCourseOptions,
  paymentType,
  subscriptionProducts = [],
  subscriptionPlans = [],
  credListOptions,
  courseOptions,
  studentCourseOptions = [],
  durations,
  addonTypeOptions = [],
  addonNameOptions = [],
  managersList,
  enrollmentTypeOptions,
}) {
  switch (field) {
    case OPTIONS.STUDENTSTATE:
      return studentStateOptions;
    case OPTIONS.TEACHERSTATE:
      return teacherStateOptions;
    case OPTIONS.GRADE:
      return gradeOptions;
    case OPTIONS.GENDER:
      return genderOptions;
    case OPTIONS.TEACHER_GENDER:
      return teacherGenderOptions;
    case OPTIONS.REGION:
      return userConfig?.regions?.map(region => ({ label: region, value: region }));
    case OPTIONS.PREFERRED_LANGUAGE:
      return userConfig?.languages?.map(language => ({
        label: language,
        value: language,
      }));
    case OPTIONS.PROGRAM:
      return programs?.map(program => ({ label: program.code, value: program.id }));
    case OPTIONS.FILTERS:
      return programs?.map(program => ({
        label: program.name,
        value: program.name.toUpperCase(),
      }));
    case OPTIONS.COMPETENCE_LEVEL:
      return Object.keys(userConfig?.competence_map)?.map(val => ({
        label: val,
        value: val,
      }));
    case OPTIONS.PERSONALITY_TYPE:
      return Object.keys(userConfig?.personality_map)?.map(val => ({
        label: val,
        value: val,
      }));
    case OPTIONS.SLOT_TAG:
      return batchConfig?.slot_tags?.map(tag => ({ label: tag, value: tag }));
    case OPTIONS.SLOT_TYPE:
      return teacherSlotTypeOptions;
    case OPTIONS.DAY:
      return dayOptions;
    case OPTIONS.TRIAL:
      return trialOptions;
    case OPTIONS.CHURN_REASON:
      return userConfig?.churn_reasons?.map(churn_reason => ({
        label: churn_reason,
        value: churn_reason,
      }));
    case OPTIONS.TEACHER_PROGRAMS:
      return userConfig?.teacher_programs?.map(teacher_program => ({
        label: teacher_program.key,
        value: teacher_program.value,
      }));
    case OPTIONS.DURATION:
      return paymentTenureOptions;
    case OPTIONS.PRODUCT_TYPE:
      return subscriptionProducts?.map(product => ({
        label: product.name,
        value: product.id,
      }));
    case OPTIONS.PAYMENT_TYPE:
      return paymentTypeOptions;
    case OPTIONS.SUBSCRIPTION_PLAN:
      return subscriptionPlans?.map(plan => ({
        label: `${plan.name} @ ${plan.total_amount}`,
        value: plan.gateway_plan_id,
      }));
    case OPTIONS.PAYMENT_CLIENT:
      return paymentClientOptions;
    case OPTIONS.CURRENCY:
      return subscriptionCurrencyOptions?.map(currency => ({
        label: currency.label,
        value: currency.value,
      }));
    case OPTIONS.UPLOAD_PAYMENT_CURRENCY:
      return uploadPaymentCurrencyOptions?.map(currency => ({
        label: currency.label,
        value: currency.value,
      }));
    case OPTIONS.ONLINE_BAND:
      return onlineBandOptions?.map(({ name, id }) => ({
        label: name,
        value: id,
      }));
    case OPTIONS.GRADE_BAND:
      return gradeOptions;

    case OPTIONS.COURSE:
      return courseOptions?.map(course => ({
        label: course.name,
        value: course,
      }));
    case OPTIONS.COURSE_TYPE:
      return Array.from(new Set(onlinePlanOptions?.map(planOptions => planOptions)))
        .sort((planOptionsA, planOptionsB) => planOptionsA - planOptionsB)
        ?.map(courseType => ({
          label: courseType.replace(/^./, courseType[0].toUpperCase()),
          value: courseType,
        }));
    case OPTIONS.NO_OF_CLASSES:
      return Array.from(new Set(onlinePlanOptions?.map(planOptions => planOptions)))
        .sort((planOptionsA, planOptionsB) => planOptionsA - planOptionsB)
        ?.map(classes => ({
          label: classes,
          value: classes,
        }));
    case OPTIONS.DYNAMIC_DURATION:
      return Array.from(new Set(onlinePlanOptions?.map(planOptions => planOptions)))
        .sort((planOptionsA, planOptionsB) => planOptionsA - planOptionsB)
        ?.map(duration => ({
          label: `${duration} Months`,
          value: duration,
        }));
    case OPTIONS.ADDON_COURSE:
      return addonCourseOptions?.map(course => ({
        label: course.name,
        value: course,
      }));
    case OPTIONS.ADDON_TYPE:
      return addonTypeOptions?.map(addon_type => ({
        label: addon_type,
        value: addon_type,
      }));
    case OPTIONS.ADDON_NAME:
      return addonNameOptions?.map(addon => ({
        label: addon.name,
        value: addon,
      }));
    case OPTIONS.CRED_STATUS:
      return teacherCredStateOptions;
    case OPTIONS.CRED:
      return credListOptions?.map(cred => ({
        label: cred.name,
        value: cred.id,
      }));
    case OPTIONS.STUDENT_DEMO_STATE:
      return studentDemoStateOptions;
    case OPTIONS.STUDENT_DEMO_LEAD_STATE:
      return studentDemoLeadStateOptions;
    case OPTIONS.PARENT_SOURCE:
      return parentSourceOptions;
    case OPTIONS.COUNTRY_CODE:
      return COUNTRY_OPTIONS;
    case OPTIONS.PAYMENT_METHOD:
      return paymentMethodOptions;
    case OPTIONS.ENROLLMENT_TYPE_OFFLINE:
      return enrollmentTypeOfflineOptions;
    case OPTIONS.CLASS_RATIO:
      return classRatioOptions;
    case OPTIONS.DYNAMIC_CLASS_RATIO:
      return dynamicClassRatioOptions(onlinePlanOptions);
    case OPTIONS.CLASSES_PER_WEEK:
      return classesPerWeekOptions;
    case OPTIONS.ACCESS_TYPE:
      return accessTypeOptions;
    case OPTIONS.STUDENT_COURSE:
      return studentCourseOptions?.map(studentCourse => ({
        label: studentCourse.course_name,
        value: studentCourse.id,
        isDisabled: studentCourse.state === COURSE_STATE.INACT,
      }));
    case OPTIONS.RM_TIMEZONES:
      return rmTimezoneOptions;
    case OPTIONS.RM_REGION:
      return rmRegionOptions;
    case OPTIONS.RM_MANAGER:
      return managersList?.map(item => ({
        label: `${item.first_name} ${item.last_name}`,
        value: item.id,
      }));
    case OPTIONS.STUDENT_COURSE_UPDATE_REASON:
      return studentCourseUpdateOptions;
    case OPTIONS.TEACHERS_LIST:
      return teachersDataOptions?.map(teacher => ({
        label: teacher.name + ' - ' + teacher.phone,
        value: teacher.id,
        disabled: teacher.disabled || false,
      }));
    case OPTIONS.ENROLLMENT_TYPE_ONLINE:
      return enrollmentTypeOptions?.map(enrollmentType => ({
        label: enrollmentType,
        value: enrollmentType,
      }));
    case OPTIONS.INDIAN_STATES:
      return INDIAN_STATE_OPTIONS;
    case OPTIONS.COUNTRY:
      return BILLING_COUNTRY_OPTIONS;
    case OPTIONS.MAPPING_MODEL_OPTIONS:
      return MAPPING_MODEL_OPTIONS;
    default:
      return [];
  }
}
